import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    IListingFilterEvent,
    IListingSetFinalModelsEvent,
    Listing,
    LISTING_SET_FILTERS,
    LISTING_SET_FINAL_MODELS,
    LISTING_UPDATE_TOTALS_EVENT,
    LISTING_UPDATE_VISIBLE_MODELS
} from '@df/catalog-ui/listing/listing';
import type { IProductDataV2, Product } from '@df/catalog/product/product';
import { DfEventService } from '@df/core/service/df-event';
import { Events } from '@df/core/service/event';

declare module '@df/catalog/product/product' {
    interface IProductDataV2 {
        isBanner?: boolean;
    }
}

@Injectable({
    providedIn: 'root'
})
export class IsBannerPluginService {
    protected destroyRef = inject(DestroyRef);
    constructor(events: Events, dfEventService: DfEventService) {
        events
            .on(LISTING_UPDATE_TOTALS_EVENT)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((listing: Listing<Product<IProductDataV2>>) => this.onListingUpdateTotals(listing));
        events
            .on(LISTING_UPDATE_VISIBLE_MODELS)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((listing: Listing<Product<IProductDataV2>>) => this.onListingUpdateVisibleModels(listing));
        events
            .on<IListingFilterEvent<Product<IProductDataV2>>>(LISTING_SET_FILTERS)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(event => this.onListingSetFilters(event));
        events
            .on<IListingSetFinalModelsEvent<Product<IProductDataV2>>>(LISTING_SET_FINAL_MODELS)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(event => this.onListingSetFinalModels(event));

        // module plugin to GTM 2 service
        dfEventService.before(_event => {
            const event = _event as any; // as any cause we don't have types for this event in GTM 1
            switch (event.id) {
                case 'dfGtmItemListView': {
                    const eventData = event.data;
                    const filteredItems = eventData.items.filter(item => {
                        if (item.product?.data?.isBanner) {
                            const position = item.position;
                            // reduce position of the items after the one
                            eventData.items.forEach(i => {
                                if (i.position > position) {
                                    i.position--;
                                }
                            });
                            // skip it
                            return false;
                        }
                        return true;
                    });
                    event.data.items = filteredItems;
                    break;
                }
            }
        });
    }

    isSorted(listing: Listing<Product<IProductDataV2>>): boolean {
        const selection = listing.sort?.getSelection();
        return selection && !selection.default ? true : false;
    }

    protected onListingUpdateTotals(listing: Listing<Product<IProductDataV2>>): void {
        if (listing.type === 'product') {
            listing.total = this.purify(listing.getFilteredModels()).length;
        }
    }

    protected onListingUpdateVisibleModels(listing: Listing<Product<IProductDataV2>>): void {
        if (listing.type === 'product') {
            listing.visibleQty = this.purify(listing.models).length;
        }
    }

    protected purify(models: Product<IProductDataV2>[]): Product<IProductDataV2>[] {
        return models?.filter(model => model.data && !model.data?.isBanner) || [];
    }

    protected onListingSetFinalModels(event: IListingSetFinalModelsEvent<Product<IProductDataV2>>): void {
        if (event.listing.type === 'product') {
            // if filters or sorting are applied
            if (this.isSorted(event.listing) || event.listing.isFiltered()) {
                event.models = this.purify(event.models);
            }
        }
    }

    protected onListingSetFilters(event: IListingFilterEvent<Product<IProductDataV2>>): void {
        if (event.listing.type === 'product') {
            // if filters are applied
            if (
                event.listing.filters.find(filter => {
                    const selectionOverride = event.overrides?.find(i => i.filter === filter)?.selection;
                    // if override is false then skip filter completely
                    return selectionOverride !== false && (selectionOverride || filter.hasSelection);
                })
            ) {
                event.models = this.purify(event.models);
            }
        }
    }
}
